import React, { useEffect, useRef } from 'react'

import { Tooltip } from '@chakra-ui/react'
import clsx from 'clsx'
import { useFormik } from 'formik'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'

import { usePutReferenceV2ApplyMutation } from '@core/app/api/applyJobV2Api'
import type { ApplyV2TempReference } from '@core/app/types/applyV2Type'
import ApplyV2Button from '@core/components/ApplyDialogV2/ApplyV2Button'
import ApplyV2InputPhone from '@core/components/ApplyDialogV2/ApplyV2InputPhone'
import { countryPhones } from '@core/components/ApplyDialogV2/ApplyV2InputPhone/constants'
import ApplyV2InputText from '@core/components/ApplyDialogV2/ApplyV2InputText'
import ApplyV2Textarea from '@core/components/ApplyDialogV2/ApplyV2Textarea'
import ApplyV2Tooltip from '@core/components/ApplyDialogV2/ApplyV2Tooltip'
import { useApplyV2Context } from '@core/utils/contexts/ApplyV2ModalContext/ApplyV2ModalContext'
import useApplyV2NavTranslations from '@core/utils/hooks/useApplyV2NavTranslations'

import './ApplyV2ReferenceStep2Edit.scss'

interface Props {
    className?: string
    onClose?: () => void
    item: ApplyV2TempReference
}

interface FormValues {
    name: string
    title: string
    companyName: string
    email: string | null
    description: string
    mobilePhone: {
        prefix: string
        number: string
    } | null
}

const baseClass = 'ApplyV2ReferenceStep2Edit'

function ApplyV2ReferenceStep2Edit({ className, onClose = () => {}, item }: Props) {
    const { getTitle } = useApplyV2NavTranslations()
    const { t } = useTranslation()
    const { storageData, postProfileRefetch } = useApplyV2Context()
    const [postReference, { isLoading: isPostLoading }] = usePutReferenceV2ApplyMutation()
    const firstRef = useRef<any>(null)

    useEffect(() => {
        if (firstRef.current) {
            firstRef.current.focus()
        }
    }, [])

    const validationSchema = Yup.object().shape({
        name: Yup.string()
            .trim()
            .required(getTitle('5653', 'Required'))
            .min(2, `${t('8336', 'The text length should be a minimum of {{value}} characters.', { value: 2 })}`)
            .max(255, `${t('8337', 'The text length should be a maximum of {{value}} characters.', { value: 255 })}`),
        title: Yup.string()
            .trim()
            .required(getTitle('5653', 'Required'))
            .min(0, `${t('8336', 'The text length should be a minimum of {{value}} characters.', { value: 0 })}`)
            .max(255, `${t('8337', 'The text length should be a maximum of {{value}} characters.', { value: 255 })}`),
        companyName: Yup.string()
            .trim()
            .required(getTitle('5653', 'Required'))
            .min(0, `${t('8336', 'The text length should be a minimum of {{value}} characters.', { value: 0 })}`)
            .max(255, `${t('8337', 'The text length should be a maximum of {{value}} characters.', { value: 255 })}`),
        description: Yup.string()
            .trim()
            // .required(getTitle('5653', 'Required'))
            .min(0, `${t('8336', 'The text length should be a minimum of {{value}} characters.', { value: 0 })}`)
            .max(5000, `${t('8337', 'The text length should be a maximum of {{value}} characters.', { value: 5000 })}`),
        email: Yup.string()
            .trim()
            .email(getTitle('728', 'Invalid email'))
            .max(255, `${getTitle('5339', 'Max')} 255`)
            .test('email-or-phone', getTitle('10472', 'Email or phone is required'), function (value) {
                // const { phoneNumber, phonePrefix } = this.parent
                const { prefix: phonePrefix, number: phoneNumber } = this.parent?.mobilePhone ?? {}

                const hasEmail = Boolean(value?.trim())
                const hasPhone = Boolean(phoneNumber?.trim())
                const hasPhonePrefix = Boolean(phonePrefix?.trim())

                // console.log('email-or-phone', 'Result -', !hasEmail && (!hasPhone || !hasPhonePrefix))
                if (!hasEmail && (!hasPhone || !hasPhonePrefix)) {
                    return this.createError({
                        message: getTitle('10472', 'Email or phone is required'),
                    })
                }
                return true
            }),
        mobilePhone: Yup.object().shape({
            prefix: Yup.string()
                .oneOf(
                    countryPhones.map((country) => country.prefix),
                    getTitle('5699', 'Invalid prefix')
                )
                .test('phone-prefix-validation', getTitle('10473', 'Phone prefix is required'), function (value) {
                    // const { email } = this.parent
                    const { email } = this.options.context ?? {}
                    const { number: phoneNumber } = this.parent?.mobilePhone || {}

                    const hasEmail = Boolean(email?.trim())
                    const hasPhone = Boolean(phoneNumber?.trim())
                    const hasPhonePrefix = Boolean(value?.trim())
                    if (!hasPhone && !hasPhonePrefix && !hasEmail) {
                        // console.log('PREFFF', this.parent, this.options.contenxt)
                        return this.createError({
                            message: getTitle('10472', 'Email or phone is required'),
                        })
                    }
                    if (!hasEmail && hasPhone && !hasPhonePrefix) {
                        return this.createError({
                            message: getTitle('10473', 'Phone prefix is required'),
                        })
                    }

                    return true
                }),
            number: Yup.string()
                .trim()
                .matches(/^[0-9]{5,12}$/, getTitle('4724', 'Your phone number is invalid'))
                .test('phone-number-validation', getTitle('6744', 'Phone number is required'), function (value) {
                    // const { email } = this.parent
                    const { email } = this.options.context ?? {}
                    // const { prefix: phonePrefix } = this.parent?.phone || {}
                    const { prefix: phonePrefix } = this.options.context?.mobilePhone || {}

                    const hasEmail = Boolean(email?.trim())
                    const hasPhone = Boolean(value?.trim())
                    const hasPhonePrefix = Boolean(phonePrefix?.trim())

                    if (!hasPhone && !hasPhonePrefix && !hasEmail) {
                        return this.createError({
                            message: getTitle('10472', 'Email or phone is required'),
                        })
                    }
                    if (hasPhone && !hasPhonePrefix) {
                        return this.createError({
                            message: getTitle('10473', 'Phone prefix is required'),
                        })
                    }
                    if (!hasEmail && hasPhonePrefix && !hasPhone) {
                        return this.createError({
                            message: getTitle('6744', 'Phone number is required'),
                        })
                    }

                    return true
                }),
        }),
    })

    const formik = useFormik<FormValues>({
        initialValues: {
            name: item.name ?? '',
            title: item.title ?? '',
            companyName: item.companyName ?? '',
            email: item.email ?? '',
            description: item.description ?? '',
            mobilePhone: item.mobilePhone
                ? {
                      prefix:
                          item.mobilePhone.prefix && !item.mobilePhone.prefix.startsWith('+')
                              ? `+${item.mobilePhone.prefix}`
                              : item.mobilePhone.prefix || '',
                      number: item.mobilePhone.number || '',
                  }
                : {
                      prefix: '',
                      number: '',
                  },
        },
        validationSchema: validationSchema,
        onSubmit: async (values, { setSubmitting, setErrors }) => {
            try {
                const uid = storageData?.postInitUid ?? ''

                const res = await postReference({
                    uid,
                    body: {
                        uid: item.uid,
                        name: values.name ? values.name.trim() : '',
                        title: values.title ? values.title.trim() : '',
                        companyName: values.companyName ? values.companyName.trim() : '',
                        email: values.email ? values.email.trim() : null,
                        description: values.description ? values.description.trim() : '',
                        mobilePhone:
                            values.mobilePhone?.number && values.mobilePhone.prefix ? values.mobilePhone : null,
                    },
                }).unwrap()
                postProfileRefetch()
            } catch (error: any) {
                if (error?.data?.errors && Array.isArray(error.data.errors)) {
                    const formikErrors: any = { ...formik.errors }

                    if (!formikErrors.mobilePhone) {
                        formikErrors.mobilePhone = { prefix: '', number: '' }
                    }

                    error.data.errors.forEach((err: any) => {
                        switch (err.field) {
                            case 'mobilePhone':
                                formikErrors.mobilePhone.prefix = err.msg
                                formikErrors.mobilePhone.number = err.msg
                                break
                            case 'mobilePhone.prefix':
                                formikErrors.mobilePhone.prefix = err.msg
                                break
                            case 'mobilePhone.number':
                                formikErrors.mobilePhone.number = err.msg
                                break
                            case 'name':
                            case 'title':
                            case 'companyName':
                            case 'email':
                            case 'description':
                                formikErrors[err.field] = err.msg
                                break
                            default:
                                formikErrors[err.field] = err.msg
                                break
                        }
                    })

                    setErrors(formikErrors)
                } else {
                    setErrors({
                        root: error.message,
                    } as any)
                }
            }
        },
        enableReinitialize: true,
        validateOnChange: true,
    })

    return (
        <form className={clsx(baseClass, className)} onSubmit={formik.handleSubmit} noValidate>
            <div className={`${baseClass}__header`}>
                <div className={`${baseClass}__header__title`}>{getTitle('1212', 'Edit reference')}</div>
                {/*<Tooltip*/}
                {/*    hasArrow*/}
                {/*    // isDisabled={isLoading}*/}
                {/*    openDelay={500}*/}
                {/*    label={getTitle('181', 'Close')}*/}
                {/*    // placement={'right'}*/}
                {/*    padding={'8px 16px'}*/}
                {/*    borderRadius={'4px'}*/}
                {/*    backgroundColor={'#19191A'}*/}
                {/*    fontWeight={600}*/}
                {/*    fontSize={'12px'}*/}
                {/*    shouldWrapChildren*/}
                {/*>*/}
                <ApplyV2Tooltip label={getTitle('181', 'Close')} placement={'top'} openDelay={500}>
                    <ApplyV2Button
                        className={`${baseClass}__header__btn`}
                        size={'small'}
                        variant="transparent"
                        onClick={onClose}
                        leftIcon={
                            <svg
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M13.6239 2.19526C13.8843 2.45561 13.8843 2.87772 13.6239 3.13807L8.8524 7.90963L13.8047 12.8619C14.0651 13.1223 14.0651 13.5444 13.8047 13.8047C13.5444 14.0651 13.1223 14.0651 12.8619 13.8047L7.90959 8.85243L3.13807 13.6239C2.87772 13.8843 2.45561 13.8843 2.19526 13.6239C1.93491 13.3636 1.93491 12.9415 2.19526 12.6811L6.96678 7.90963L2.37605 3.31886C2.1157 3.05851 2.11571 2.6364 2.37606 2.37605C2.63641 2.11571 3.05852 2.11571 3.31887 2.37606L7.90959 6.96682L12.6811 2.19526C12.9415 1.93491 13.3636 1.93491 13.6239 2.19526Z"
                                    fill="#19191A"
                                />
                            </svg>
                        }
                    />
                </ApplyV2Tooltip>
                {/*</Tooltip>*/}
            </div>
            <ApplyV2InputText
                ref={firstRef}
                name="name"
                label={getTitle('6745', 'Full name')}
                value={formik.values.name}
                onChange={(e) => {
                    formik.setFieldTouched('name', true)
                    formik.handleChange(e)
                }}
                onBlur={formik.handleBlur}
                placeholder={getTitle('6750', 'Enter referent full name')}
                errorMessage={formik.touched.name && formik.errors.name}
                isRequired
            />
            <ApplyV2InputText
                name="companyName"
                label={getTitle('312', 'Company name')}
                value={formik.values.companyName}
                onChange={(e) => {
                    formik.setFieldTouched('companyName', true)
                    formik.handleChange(e)
                }}
                onBlur={formik.handleBlur}
                placeholder={getTitle('714', 'Enter referent company name')}
                errorMessage={formik.touched.companyName && formik.errors.companyName}
                isRequired
            />
            <ApplyV2InputText
                name="title"
                label={getTitle('171', 'Title')}
                value={formik.values.title}
                onChange={(e) => {
                    formik.setFieldTouched('title', true)
                    formik.handleChange(e)
                }}
                onBlur={formik.handleBlur}
                placeholder={getTitle('696', 'Enter referent title')}
                errorMessage={formik.touched.title && formik.errors.title}
                isRequired
            />
            <ApplyV2InputPhone
                name="mobilePhone"
                label={getTitle('137', 'Mobile phone')}
                value={formik.values.mobilePhone}
                onChange={(phoneValue) => {
                    formik.setFieldTouched('mobilePhone', true)
                    formik.setFieldValue('mobilePhone', phoneValue)
                }}
                onBlur={formik.handleBlur}
                errorMessage={formik.touched.mobilePhone && formik.errors.mobilePhone}
                isRequired={!formik.values.email}
            />
            <ApplyV2InputText
                name="email"
                label={getTitle('92', 'Email')}
                value={formik.values.email}
                onChange={(e) => {
                    formik.setFieldTouched('email', true)
                    formik.handleChange(e)
                }}
                onBlur={formik.handleBlur}
                placeholder={getTitle('727', 'Enter referent email')}
                errorMessage={formik.touched.email && formik.errors.email}
                isRequired={!(formik.values.mobilePhone?.prefix && formik.values.mobilePhone?.number)}
            />
            <ApplyV2Textarea
                name="description"
                label={getTitle('606', 'Description')}
                // isLoading={true}
                value={formik.values.description}
                // placeholder={getTitle('7606', 'Description text')}
                onChange={(v) => {
                    formik.setFieldValue('description', v)
                }}
                onBlur={formik.handleBlur}
                placeholder={getTitle('1650', 'Enter description')}
                errorMessage={formik.touched.description && formik.errors.description}
                // isRequired
                // description={'daw awd'}
            />
            {/*<ApplyV2InputTextarea*/}
            {/*    name="description"*/}
            {/*    label={getTitle('606', 'Description')}*/}
            {/*    value={formik.values.description}*/}
            {/*    onChange={(e) => {*/}
            {/*        formik.setFieldTouched('description', true)*/}
            {/*        formik.handleChange(e)*/}
            {/*    }}*/}
            {/*    onBlur={formik.handleBlur}*/}
            {/*    placeholder={getTitle('1650', 'Enter description')}*/}
            {/*    errorMessage={formik.touched.description && formik.errors.description}*/}
            {/*/>*/}
            <div className={`${baseClass}__btns`}>
                <ApplyV2Button
                    type={'reset'}
                    variant="transparent"
                    className={`${baseClass}__btns--btn ${baseClass}__btns--btn--submit`}
                    // isLoading={formik.isSubmitting}
                    onClick={() => {
                        // console.log(formik.values, formik.touched, formik.errors)
                        // const touchedFields = {} as any
                        // Object.keys(formik.values).forEach((key) => {
                        //     touchedFields[key] = true
                        // })
                        //
                        // formik.setTouched(touchedFields)
                        formik.resetForm()
                        onClose()
                    }}
                >
                    {getTitle('416', 'Cancel')}
                </ApplyV2Button>
                <ApplyV2Button
                    type={'submit'}
                    // type={'button'}
                    variant="primary"
                    className={`${baseClass}__btns--btn ${baseClass}__btns--btn--submit`}
                    isLoading={formik.isSubmitting || isPostLoading}
                    isDisabled={!formik.isValid || formik.isSubmitting || !formik.dirty}
                    // isDisabled={!formik.isValid || !formik.dirty}
                    // onClick={() => {
                    //     console.log('Submit clicked', formik.values)
                    //     console.log('Formik state:', formik.touched)
                    //     console.log('Formik errors:', formik.errors)
                    //     console.log('Formik isValid:', formik.isValid, formik.dirty)
                    // }}
                >
                    {getTitle('12', 'Save')}
                </ApplyV2Button>
            </div>
        </form>
    )
}

export default ApplyV2ReferenceStep2Edit
